/**
 * `profile` — docs/SPEC.md §profile, faza 1.
 *
 * Bu ekran D-003 ning VIZUAL IFODASI: ikkita statistika kartasi ataylab
 * alohida turadi.
 *
 *   charm  sotib olinadi (yuboruvchi to'laydi)  →  --pink
 *   level  sotib olinmaydi (efir + streak)      →  avatar halqasi, --blue
 *
 * Prototipdan IKKI CHETLANISH (docs/SPEC.md §1):
 *   X-8  avatar halqasi LEVEL progressini ko'rsatadi, charm'ni emas
 *   X-8  charm kartasida `Lv … gacha` izohi YO'Q — o'rniga reytingdagi o'rin
 */

import type { GiftShowcaseItem, MeResponse } from '@voice/api-client';
import { useNavigate } from 'react-router-dom';

import { useBackButton } from '../app/useBackButton';
import { uz } from '../i18n/uz';
import { useMe, useMyGifts } from '../lib/api/useMe';
import { compact, fmt } from '../lib/format';
import { Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import { paletteVars } from '@voice/ui-tokens';
import './screens.css';

/** Kam uchraydigan sovg'a — oltin chegara (prototip: `n < 10`). */
const RARE_BELOW = 10;

export function Profile() {
  const navigate = useNavigate();
  const { data: me, isLoading, isError } = useMe();
  const gifts = useMyGifts();
  const toast = useToast();

  const goBack = () => navigate('/');
  useBackButton(true, goBack);

  if (isLoading) {
    return (
      <div className="screen">
        <div className="empty">{uz.common.loading}</div>
      </div>
    );
  }

  if (isError || !me) {
    return (
      <div className="screen">
        <div className="empty">{uz.profile.loadFailed}</div>
      </div>
    );
  }

  async function copyId(id: number): Promise<void> {
    try {
      await navigator.clipboard.writeText(String(id));
      toast.show(uz.profile.idCopied);
    } catch {
      // Telegram WebView'da clipboard rad etilishi mumkin — jim qolmaymiz.
      toast.show(uz.profile.idCopyFailed);
    }
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <div className="screen-bar">
        <button type="button" className="icon-btn" onClick={goBack} aria-label={uz.nav.home}>
          <Icon name="back" size={16} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate('/settings')}
          aria-label={uz.settings.title}
        >
          <Icon name="sliders" size={17} color="var(--fg-80)" />
        </button>
      </div>

      <div className="hero">
        <LevelRing me={me} />

        <h1 className="hero__name">{me.first_name}</h1>

        <button type="button" className="id-chip tnum" onClick={() => void copyId(me.id)}>
          {`ID\u00A0${fmt(me.id)}`}
          <Icon name="copy" size={11} />
        </button>

        <Badges me={me} />
      </div>

      <div className="stats">
        <CharmCard me={me} />
        <AirCard me={me} />
      </div>

      <VipCard me={me} />

      <div className="section-head">
        <div className="section-head__title">{uz.profile.gifts}</div>
        <div className="section-head__rule" />
        {gifts.data && gifts.data.items.length > 0 && (
          <div className="section-head__count tnum">
            {fmt(gifts.data.items.reduce((sum, g) => sum + g.count, 0))}
          </div>
        )}
      </div>

      <Showcase items={gifts.data?.items} isLoading={gifts.isLoading} />

      <div className="screen-tail" />
    </div>
  );
}

/* ── Avatar halqasi — LEVEL progressi (D-040) ── */

function LevelRing({ me }: { me: MeResponse }) {
  return (
    <div className="ring">
      {/* `--pct` serverdan hisoblangan qiymat — formula TS'da takrorlanmaydi. */}
      <div className="ring__track" style={{ ['--pct' as string]: `${me.level.percent}%` }} />
      <div className="ring__gap" />
      <div className="ring__face" style={paletteVars(me.palette_idx)}>
        {me.first_name.slice(0, 1)}
      </div>
      <div className="ring__level">{`${uz.profile.level} ${me.level.level}`}</div>
    </div>
  );
}

/* ── Badge qatori — faqat HAQIQIY ma'lumot ────────────────────────────
 * Prototipda "Top host" va "48 do'st" ham bor. Ular faza 2 va 3 dagi
 * ma'lumotga tayanadi — bo'lmagan raqamni ko'rsatmaymiz. */

function Badges({ me }: { me: MeResponse }) {
  const items = [
    me.vip && { key: 'vip', mod: 'badge--vip', icon: 'crown' as const, text: me.vip.name },
    me.streak_days > 0 && {
      key: 'streak',
      mod: 'badge--streak',
      icon: 'flame' as const,
      text: `${fmt(me.streak_days)} kun`,
    },
    me.air_minutes > 0 && {
      key: 'air',
      mod: 'badge--air',
      icon: 'mic' as const,
      text: uz.profile.airHours(fmt(Math.floor(me.air_minutes / 60))),
    },
  ].filter(Boolean) as {
    key: string;
    mod: string;
    icon: 'crown' | 'flame' | 'mic';
    text: string;
  }[];

  if (items.length === 0) return null;

  return (
    <div className="badges">
      {items.map((b) => (
        <div key={b.key} className={`badge ${b.mod}`}>
          <Icon name={b.icon} size={11} />
          {b.text}
        </div>
      ))}
    </div>
  );
}

/* ── Charm kartasi ────────────────────────────────────────────────────
 * Progress chizig'i YO'Q. Prototipda u `Lv 35 gacha 16K charm` izohi bilan
 * juft edi, X-8 esa o'sha bog'lanishni bekor qildi — charm'ning "keyingi
 * bosqichi" degan tushuncha mahsulotda mavjud emas. Ma'nosiz chiziq
 * chizishdan ko'ra chizmaslik to'g'ri (Q-041 da so'ralgan). */

function CharmCard({ me }: { me: MeResponse }) {
  return (
    <div className="stat stat--charm">
      <div className="stat__label">
        <Icon name="heart" size={12} color="var(--pink)" />
        {uz.profile.charm}
      </div>
      <div className="stat__value tnum">{compact(me.charm)}</div>
      <div className="stat__caption">
        {me.charm_rank === null ? uz.profile.charmEmpty : uz.profile.charmRank(fmt(me.charm_rank))}
      </div>
    </div>
  );
}

/* ── Efir kartasi — level progressi shu yerda ko'rinadi ── */

function AirCard({ me }: { me: MeResponse }) {
  return (
    <div className="stat stat--air">
      <div className="stat__label">
        <Icon name="mic" size={12} color="var(--blue)" />
        {uz.profile.air}
      </div>
      <div className="stat__value tnum">
        {uz.profile.airHours(fmt(Math.floor(me.air_minutes / 60)))}
      </div>
      <div className="bar">
        <div className="bar__fill bar__fill--air" style={{ width: `${me.level.percent}%` }} />
      </div>
      <div className="stat__caption">
        {me.streak_days > 0 ? uz.profile.streak(fmt(me.streak_days)) : uz.profile.streakNone}
      </div>
    </div>
  );
}

/* ── VIP kartasi ── */

/**
 * VIP kartasi — hozircha FAQAT holat ko'rsatadi, sotib olinmaydi.
 *
 * Sotib olish oqimi faza 1 qamrovida yo'q (`docs/PHASES.md` ro'yxatida
 * VIP yozilmagan). Ilgari bu karta tugma bo'lib `/shop` ga olib borardi,
 * u yerda esa faqat diamond paketlari — ya'ni "Faollashtirish" bosilardi
 * va hech narsa bo'lmasdi. Va'da berib bajarmaslikdan ko'ra sukut yaxshi:
 * tugma olib tashlandi, o'rniga "tez orada".
 *
 * Oqim qo'shilganda bu yana `<button>` bo'ladi (Q-044).
 */
function VipCard({ me }: { me: MeResponse }) {
  const active = me.vip;
  const until = active
    ? new Date(active.expires_at).toLocaleDateString('uz-UZ', { day: 'numeric', month: 'long' })
    : null;

  return (
    <div className="vip-card">
      <Icon name="crown" size={20} color="var(--gold)" />
      <div className="vip-card__body">
        <div className="vip-card__title">{active ? active.name : uz.profile.vipTitle}</div>
        <div className="vip-card__hint">
          {active ? uz.profile.vipActive(until ?? '') : uz.profile.vipHint}
        </div>
      </div>
      {!active && <div className="vip-card__soon">{uz.profile.vipSoon}</div>}
    </div>
  );
}

/* ── Sovg'a vitrinasi ── */

function Showcase({
  items,
  isLoading,
}: {
  // `| undefined` ataylab: `exactOptionalPropertyTypes` yoqilgan, ya'ni
  // "maydon yo'q" bilan "maydon undefined" bir xil emas.
  items: GiftShowcaseItem[] | undefined;
  isLoading: boolean;
}) {
  if (isLoading) return <div className="empty">{uz.common.loading}</div>;
  if (!items || items.length === 0) return <div className="empty">{uz.profile.giftsEmpty}</div>;

  return (
    <div className="showcase">
      {items.map((gift) => (
        <div
          key={gift.gift_id}
          className={`showcase__cell${gift.count < RARE_BELOW ? ' showcase__cell--rare' : ''}`}
          title={gift.name}
        >
          <div className="showcase__icon">{gift.icon}</div>
          <div className="showcase__count tnum">{`×${fmt(gift.count)}`}</div>
        </div>
      ))}
    </div>
  );
}
